import i18n from '@ConfigLoader/I18n';
import cn from 'classnames';
import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

// STYLES
import styles from '@UILayer/UILayer.scss';

// MANAGERS
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import { getAuthenticateGameConfig, getBetState, getGlobalStyles, getSymbolConfiguration } from '@Managers/Redux/AppState/AppState';
import { useAppSelector } from '@Managers/Redux/Hooks';

// TYPES
import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import { ESymbolsPaytableOrder } from '@ConfigLoader/Configs';
import { CurrencyUtils } from '@Core/Currencies/CurrencyUtils';
import { customOrder, getBlockDescription, multiplierCompare } from './Paytable.functions';
import { IData, IPaytable, ISymbolOrderItem, PAYTABLE_STATE_DEFAULTS } from './Paytable.types';

import { DOM_ID } from '@UILayer/UILayer.types';
import PaytableSymbolDescription from './Paytable.symbolDescription';

const Paytable: React.FC<{}> = () => {
    const [state, setState] = useReducer(
        (state: IPaytable, newState: any) => ({
            ...state,
            ...newState
        }),
        PAYTABLE_STATE_DEFAULTS
    );

    const gameStoreAuthenticateGameConfig = useAppSelector(getAuthenticateGameConfig);
    const gameStoreBetState = useAppSelector(getBetState);
    const gameStoreSymbolConfiguration = useAppSelector(getSymbolConfiguration);
    const globalStyles = useAppSelector(getGlobalStyles);
    const { t } = useTranslation();
    const getPayLineValue = (multiplier: number, isRolesPaytable: boolean) =>
        CurrencyUtils.formatRawPaytableValue(gameStoreBetState.value.raw, multiplier, isRolesPaytable);
    const containerCN = cn(
        styles.settings_content_paytable,
        globalStyles.orientation,
        globalStyles.handMode,
        globalStyles.device,
        globalStyles.wideDevice,
        globalStyles.isTablet
    );

    useEffect(() => {
        const symbolsData = gameStoreAuthenticateGameConfig.symbols;
        const symbolsConfiguration = gameStoreSymbolConfiguration;
        const symbols: IData = {};
        const symbolsOrderType = ConfigLoader.getConfiguration(EConfigurationNames.GAME).symbolsPaytableOrder;
        let symbolsOrder: Array<ISymbolOrderItem> = [];

        const translationPack = i18n.getDataByLanguage(ConfigLoader.language)?.translation as any;
        const symbolsDescripton = translationPack?.['PAYTABLE_SYMBOLS_DESCRIPTION'] ? translationPack['PAYTABLE_SYMBOLS_DESCRIPTION'] : {};

        symbolsData[0].forEach((element) => {
            symbols[element.symbolId] = element;
            symbolsOrder.push({ id: element.symbolId, multiplier: element.paytable ? element.paytable[0].multiplier[0] : 0 });
        });

        symbolsConfiguration.forEach((element) => {
            symbols[element.id] = {
                ...symbols[element.id],
                name: element.symbolName,
                description: symbolsDescripton[element.id] ? symbolsDescripton[element.id].description : false
            };
        });

        switch (symbolsOrderType) {
            case ESymbolsPaytableOrder.MULTIPLIER_DESCENDING:
                symbolsOrder.sort(multiplierCompare).reverse();
                break;
            default:
                if (Array.isArray(symbolsOrderType)) {
                    symbolsOrder = customOrder(symbolsOrder, symbolsOrderType);
                }

                break;
        }

        if (symbols) {
            const symbolsData = [...symbolsOrder];
            let maxPaytableString: number = 0;
            let maxPaytableStringBlockIndex: number = 0;

            symbolsData.forEach((val, Blockindex) => {
                const symbolID = symbols[val.id].symbolId;
                const symbolSpineConfig = symbols[val.id];
                const basePaytable = symbolSpineConfig.paytable;
                const rolePaytable = symbolSpineConfig.roles?.find((role) => !role.gameTypes || role.gameTypes.includes(1))?.paytable;
                const isRolesPaytable = !basePaytable && !!rolePaytable;
                const symbolPaytable = basePaytable ?? rolePaytable ?? [];
                const blockDescription = getBlockDescription({ paylines: symbolPaytable, symbolId: symbolID, isRolesPaytable }, getPayLineValue);

                blockDescription.dom.forEach((val, LineIndex) => {
                    if (val.lineLength > maxPaytableString) {
                        maxPaytableString = val.lineLength;
                        maxPaytableStringBlockIndex = Blockindex;
                    }
                });

                const symbolDescription = symbols[val.id].description;
                const symbolDescriptionInner = !!(symbolDescription && symbolPaytable?.length === 0);

                symbolsData[Blockindex].symbolID = symbolID;
                symbolsData[Blockindex].symbolName = symbolID && symbols[val.id].name ? t(`${symbols[val.id].name}`) : symbolID;
                symbolsData[Blockindex].symbolDescription = symbolDescription;
                symbolsData[Blockindex].symbolDescriptionInner = symbolDescriptionInner;
                symbolsData[Blockindex].blockDescription = blockDescription;
            });

            setState({ data: symbols, order: symbolsData, maxPaytableString: { block: maxPaytableStringBlockIndex, value: maxPaytableString } });
        }
    }, [gameStoreAuthenticateGameConfig, gameStoreSymbolConfiguration]);

    function setGlobalPayTableFontSize(value: number) {
        setState({ blockFontSize: value });
    }

    return (
        <div className={containerCN}>
            {state.order.map((item, index) => {
                return (
                    <div
                        aria-description={`${item.symbolName} : ${item.symbolDescriptionInner ? item.symbolDescription : item.blockDescription?.raw}`}
                        tabIndex={0}
                        key={`paytable-symbol_${item.symbolID}`}
                        className={cn(styles.paytable_item, globalStyles.wcaMode)}
                    >
                        <div className={styles.paytable_item_content}>
                            <div className={styles.paytable_item_symbol}>
                                <img
                                    src={`${ConfigLoader.gameResourcePath}/bitmaps/ui/symbols/id_${item.symbolID}.webp?v=${ConfigLoader.getConfiguration(EConfigurationNames.VERSION).cacheVersion}`}
                                    onError={(e) => {
                                        const img = e.currentTarget as HTMLImageElement;
                                        if (!img.dataset.fallback) {
                                            img.dataset.fallback = 'true';
                                            img.src = `${ConfigLoader.gameResourcePath}/bitmaps/ui/symbols/id_${item.symbolID}.png?v=${ConfigLoader.getConfiguration(EConfigurationNames.VERSION).cacheVersion}`;
                                        }
                                    }}
                                />
                            </div>
                            <div
                                className={cn(styles.paytable_item_info, globalStyles.screenSize, globalStyles.orientation)}
                                style={state.blockFontSize && !item.symbolDescriptionInner ? { fontSize: `${state.blockFontSize}px` } : {}}
                            >
                                {item.symbolDescriptionInner ? (
                                    <p
                                        id={DOM_ID.PAYTABLE_SPECIAL_SYMBOL_DESCRIPTION}
                                        className={styles.paytable_special_symbol_description}
                                        style={state.blockFontSize ? { fontSize: `${state.blockFontSize - state.blockFontSize / 3}px` } : {}}
                                    >
                                        {item.symbolDescription}
                                    </p>
                                ) : (
                                    <PaytableSymbolDescription
                                        maxStringData={state.maxPaytableString}
                                        paytableData={item.blockDescription}
                                        paytableIndex={index}
                                        textFieldCallback={(value: number) => setGlobalPayTableFontSize(value)}
                                    />
                                )}
                            </div>

                            {!item.symbolDescriptionInner && item.symbolDescription ? (
                                <div
                                    className={styles.paytable_item_desc}
                                    id={DOM_ID.PAYTABLE_SPECIAL_SYMBOL_DESCRIPTION}
                                    style={state.blockFontSize ? { fontSize: `${state.blockFontSize - state.blockFontSize / 3}px` } : {}}
                                >
                                    {item.symbolDescription}
                                </div>
                            ) : null}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default Paytable;

//style={state.blockFontSize ? { fontSize: `${state.blockFontSize - state.blockFontSize / 3}px` } : {}}
